import { shortenRunDisplayId } from './progressDataTransformer';

// The mobile run-detail PAGE is addressed by a search param, not a path segment.
//
// RSAAnalysis renders in two hosts — the standalone /RSA-Analysis route and,
// embedded, inside ProjectView at /projects/:id — so a path route like
// /RSA-Analysis/run/:id would only ever cover the first one. ProjectView already
// treats the URL as the single source of truth for RSA sub-state (rsa_view /
// rsa_tab) and its updateRsaRouteState() rebuilds the query from the CURRENT
// search while touching only its own keys, so this one rides along untouched
// through tab and view changes.
//
// Three modules share the key: RSAAnalysis (opens the page and derives it),
// MobileBottomBar (its Back button closes the page), and their tests. It lives
// here so none of them has to import from the others.

export const RSA_RUN_QUERY_KEY = 'rsa_run';

/** The open run's id, or null when the run page is closed. */
export const getRsaRunFromSearch = (search = '') => {
  const raw = new URLSearchParams(search).get(RSA_RUN_QUERY_KEY);
  return String(raw ?? '').trim() || null;
};

/** Same query string with the run page opened on `runId`. */
export const searchWithRsaRun = (search = '', runId) => {
  const params = new URLSearchParams(search);
  params.set(RSA_RUN_QUERY_KEY, String(runId));
  const next = params.toString();
  return next ? `?${next}` : '';
};

/** Same query string with the run page closed. */
export const searchWithoutRsaRun = (search = '') => {
  const params = new URLSearchParams(search);
  params.delete(RSA_RUN_QUERY_KEY);
  const next = params.toString();
  return next ? `?${next}` : '';
};

/**
 * Bottom-bar Back -> run page. The bar knows a run page is open (it reads the
 * param) but not how it was opened, and only RSAAnalysis knows whether it
 * pushed a history entry it may safely pop. So the bar ASKS rather than acts:
 * the event is cancelable, and a handler that closes the page calls
 * preventDefault() to claim it. dispatchEvent() then returns false, which is
 * the bar's signal to stand down.
 *
 * Without the handshake a stale param (deep link into a non-RSA project, where
 * RSAAnalysis never mounts) would leave Back doing nothing at all.
 */
export const RSA_RUN_PAGE_BACK_EVENT = 'rsa:mobile-run-page-back';

/**
 * Dashboard deep link (2026-09-08): /projects/<projectId>?run=<run_id> opens
 * the project's RSA analysis Dashboard tab with that run tile selected. The
 * Multi-project runs view links every row here. Distinct from `rsa_run`
 * (above), which is the MOBILE run page; `run` is a one-shot selection that
 * RSAAnalysis consumes and then strips from the URL, so a later click on
 * another tile is not undone by a refresh.
 */
export const RSA_DEEP_LINK_RUN_KEY = 'run';

/** The run id a deep link asks to select, or null. */
export const getDeepLinkRunFromSearch = (search = '') => {
  const raw = new URLSearchParams(search).get(RSA_DEEP_LINK_RUN_KEY);
  return String(raw ?? '').trim() || null;
};

/** Same query string without the deep-link key. */
export const searchWithoutDeepLinkRun = (search = '') => {
  const params = new URLSearchParams(search);
  params.delete(RSA_DEEP_LINK_RUN_KEY);
  const next = params.toString();
  return next ? `?${next}` : '';
};

/** Path + query of a run's dashboard: "/projects/33?run=project_33_run_…". */
export const runDashboardHref = (projectId, runId) => {
  const pid = String(projectId ?? '').trim().replace(/^project_/, '');
  const rid = String(runId ?? '').trim();
  if (!pid) return null;
  return `/projects/${encodeURIComponent(pid)}${rid ? `?${RSA_DEEP_LINK_RUN_KEY}=${encodeURIComponent(rid)}` : ''}`;
};

/**
 * The run a dashboard deep link (?run=<id>) points at, matched on the full id
 * first, then the run token / display id (the queue mints
 * "project_<id>_run_pyexisting_<stamp>"; the list may hold either form).
 */
export const pickDeepLinkRun = (runs, runId) => {
  const wanted = String(runId ?? '').trim();
  if (!wanted || !Array.isArray(runs) || runs.length === 0) return null;
  const ids = (r) =>
    [r?.run_id, r?.full_run_id, r?.run_key, r?.id, r?.run_display_id]
      .filter((v) => v != null && v !== '')
      .map(String);
  const exact = runs.find((r) => ids(r).includes(wanted));
  if (exact) return exact;
  const wantedShort = shortenRunDisplayId(wanted);
  return (
    runs.find((r) =>
      ids(r).some(
        (id) =>
          id.endsWith(wanted) ||
          wanted.endsWith(id) ||
          (wantedShort && shortenRunDisplayId(id) === wantedShort),
      ),
    ) || null
  );
};

/**
 * Which RSA tab a project URL should open on. An explicit `rsa_tab` wins;
 * otherwise a deep-linked run lands on the Dashboard; otherwise the caller's
 * default (ProjectView passes 'upload').
 */
export const resolveInitialRsaTab = (search = '', fallback = 'upload') => {
  const params = new URLSearchParams(search);
  const explicit = String(params.get('rsa_tab') ?? '').trim();
  if (explicit) return explicit;
  return getDeepLinkRunFromSearch(search) ? 'dashboard' : fallback;
};
